import React, { useState, useEffect } from 'react';
import { profileService, UserPost } from '../../services/profile.service';

interface PostsTabProps {
  userId: string;
  isOwnProfile: boolean;
}

export function PostsTab({ userId, isOwnProfile }: PostsTabProps) {
  const [posts, setPosts] = useState<UserPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadPosts();
  }, [userId, page]);

  const loadPosts = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await profileService.getUserPosts(userId, page, 20);
      if (page === 1) {
        setPosts(data);
      } else {
        setPosts((prev) => [...prev, ...data]);
      }
      setHasMore(data.length === 20);
    } catch (err: any) {
      setError(err.message || 'Error al cargar los posts');
    } finally {
      setLoading(false);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
        <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8 text-center">
        <svg className="w-16 h-16 mx-auto text-gray-400 dark:text-gray-600 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
        <p className="text-gray-600 dark:text-gray-400">
          {isOwnProfile ? 'Aún no has creado ningún post' : 'Este usuario no tiene posts públicos'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {posts.map((post) => (
        <div
          key={post.id}
          className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="flex-1">
              {post.group && (
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    En {post.group.name}
                  </span>
                </div>
              )}
              {post.is_pinned && (
                <div className="flex items-center gap-1 mb-2">
                  <svg className="w-4 h-4 text-violet-600" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.617a1 1 0 01.894-1.788l1.599.799L11 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c-.25.78.137 1.638.918 1.888l.894.298c.779.26 1.638-.136 1.888-.918l.818-2.552a3.989 3.989 0 00-.668-3.515 3.989 3.989 0 00-3.03-1.527z" />
                  </svg>
                  <span className="text-xs font-medium text-violet-600 dark:text-violet-400">
                    Fijado
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  post.visibility === 'public'
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                    : post.visibility === 'followers'
                    ? 'bg-violet-100 dark:bg-violet-900/30 text-violet-800 dark:text-violet-200'
                    : 'bg-fuchsia-100 dark:bg-fuchsia-900/30 text-fuchsia-800 dark:text-fuchsia-200'
                }`}
              >
                {post.visibility === 'public' ? '🌍 Público' : post.visibility === 'followers' ? '👥 Seguidores' : '👪 Grupo'}
              </span>
            </div>
          </div>

          <p className="text-gray-900 dark:text-white whitespace-pre-wrap mb-3">
            {post.content}
          </p>

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {new Date(post.created_at).toLocaleDateString('es-ES', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
            {post.updated_at !== post.created_at && (
              <span className="italic">Editado</span>
            )}
          </div>
        </div>
      ))}

      {hasMore && (
        <button
          onClick={() => setPage((p) => p + 1)}
          disabled={loading}
          className="w-full py-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
          )}
          {loading ? 'Cargando...' : 'Cargar más'}
        </button>
      )}
    </div>
  );
}
